import type {
	IArtworkMinimum,
	GraphQLObject,
	IElasticObjectCardSource,
} from '@metmuseum/types';
import { routes } from '@metmuseum/routes';

export type {
	IElasticObjectCardSource,
	IElasticObjectSearchSource,
} from '@metmuseum/types';

export const OBJECT_CARD_SOURCE_FIELDS = [
	'accessionNumber',
	'artistPrimary',
	'artistSecondary',
	'artists',
	'crdId',
	'culture',
	'dated',
	'extendedStatuses',
	'medium',
	'period',
	'thumbnailAltText',
	'thumbnailUrl',
	'title',
] satisfies Array<keyof IElasticObjectCardSource>;

export type LofiArtworkObjectModelInput =
	GraphQLObject | IArtworkMinimum | IElasticObjectCardSource;

/*
	Normalizes object data from the GraphQL and Elastic endpoints into a single shape.
	An object can be passed into lofiArtworkObjectModel multiple times without mutation.
*/

export const lofiArtworkObjectModel = (
	object: LofiArtworkObjectModelInput,
): IArtworkMinimum => {
	const formattedObject = {} as IArtworkMinimum;

	formattedObject.accessionNumber = object.accessionNumber;

	if ('culture' in object) {
		formattedObject.culture = object?.culture;
	}

	// TODO: eventually make this "dated" everywhere for consistency? (That's the CRD name)
	if ('date' in object) {
		// api's "date" field is just a wrapper for "dated" (from CRD - a free from string like: "probably ca. 1658–62")
		formattedObject.date = object?.date;
	} else if ('dated' in object) {
		// for Elastic, I kept the CRD naming, "dated", so we can fallback to that, as it is the same field/data
		formattedObject.date = object?.dated;
	}

	if ('medium' in object) {
		formattedObject.medium = object?.medium;
	}

	//artists (detailed artist information)
	// TODO: the GraphQL-shape -> IObjectPageArtist mapping below duplicates the one in
	// api/src/search/search-object.service.ts:formatForSearch. Today web fetches
	// directly from collection-api's GraphQL endpoint (COLLECTION_GRAPHQL_URL) which
	// is why it has to normalize here. The plan is to make NestJS the source-of-truth
	// API for collection object data; once web fetches through met-js api (instead of
	// calling collection-api directly), the api will be the only place this normalization
	// runs and this whole branch can be deleted - at that point web only sees
	// pre-normalized IObjectPageArtist[] coming back.
	if ('artists' in object && object.artists) {
		// Check if this is already an array of IObjectPageArtist (from object page,
		// or from Elastic _source post-DIGAPPDEV-2568 - both shapes are pre-normalized).
		if (Array.isArray(object.artists)) {
			formattedObject.artists = object.artists;
		}
		// GraphQL structure with 'all' property - needs normalization in-place until the
		// migration above eliminates the need for this.
		else if ('all' in object.artists && Array.isArray(object.artists.all)) {
			formattedObject.artists = object.artists.all.map((artist) => ({
				displayName: artist.displayName,
				constituentID: artist.constituentID,
				nationality: artist.nationality || '',
				prefix: artist.prefix || '',
				role: artist.role || '',
				roleID: artist.roleID || 0,
			}));
		}
	}

	//allArtistsFormatted
	// TODO: discuss with larger team...
	// primaryArtist is not in artists.all in graphql, and we were accidentally omitting it
	// here, I added primaryArtist back, but only as a fallback?
	if ('allArtistsFormatted' in object) {
		formattedObject.allArtistsFormatted = object.allArtistsFormatted;
	} else if ('artists' in object && object.artists) {
		if (Array.isArray(object.artists)) {
			// Elastic Object _source with detailed artists, or object-page shape
			formattedObject.allArtistsFormatted = object.artists.map(
				(x) => x.displayName,
			);
		} else if ('all' in object.artists) {
			formattedObject.allArtistsFormatted = object?.artists?.all?.map(
				(x) => x.displayName,
			);
		}
	} else if ('artistPrimary' in object) {
		// Elastic Object _source (no detailed artists - e.g. not yet reindexed)
		formattedObject.allArtistsFormatted = [object.artistPrimary].filter(
			Boolean,
		);
		if ('artistSecondary' in object) {
			formattedObject.allArtistsFormatted = [
				object.artistPrimary,
				...object.artistSecondary,
			].filter(Boolean);
		}
	}

	//contextualLabel
	if ('contextualLabel' in object) {
		formattedObject.contextualLabel = object.contextualLabel;
	}

	//creditLine
	if ('creditLine' in object) {
		formattedObject.creditLine = object.creditLine;
	}

	//dimensions
	if ('dimensions' in object) {
		formattedObject.dimensions = object.dimensions;
	}

	//exhibitionLabels
	if ('exhibitionLabels' in object) {
		formattedObject.exhibitionLabels = object.exhibitionLabels;
	}

	//image
	if ('image' in object) {
		formattedObject.image = object.image;
	} else if ('images' in object) {
		formattedObject.image = object.images?.primary?.mainImageUrl;
	} else if ('thumbnailUrl' in object) {
		formattedObject.image = object.thumbnailUrl;
	}

	//imageAlt
	if ('imageAlt' in object) {
		formattedObject.imageAlt = object.imageAlt;
	} else if ('thumbnailAltText' in object) {
		formattedObject.imageAlt = object.thumbnailAltText;
	} else if ('images' in object) {
		formattedObject.imageAlt = object.images?.primary?.altText;
	}

	//objectID
	if ('objectID' in object) {
		formattedObject.objectID = object.objectID;
	} else if ('crdId' in object) {
		formattedObject.objectID = object.crdId; // Elastic
	}

	//period
	if ('period' in object) {
		formattedObject.period = object.period;
	}

	//primaryArtist
	if ('primaryArtist' in object) {
		formattedObject.primaryArtist = object.primaryArtist;
	} else if (
		'artists' in object &&
		object.artists &&
		!Array.isArray(object.artists)
	) {
		// Only access 'primary' if it's an object with that property (not an array)
		if ('primary' in object.artists) {
			formattedObject.primaryArtist = object?.artists?.primary?.displayName;
		}
	} else if ('artistPrimary' in object && object.artistPrimary) {
		formattedObject.primaryArtist = object.artistPrimary;
	}

	//primaryImageRestricted
	if ('primaryImageRestricted' in object) {
		formattedObject.primaryImageRestricted = object.primaryImageRestricted;
	} else if ('images' in object) {
		formattedObject.primaryImageRestricted =
			!object.images?.primary?.openAccess &&
			!!object.images?.primary?.mainImageUrl;
	}

	//rightsAndReproduction
	if ('rightsAndReproduction' in object) {
		formattedObject.rightsAndReproduction = object.rightsAndReproduction;
	}

	//title
	if ('title' in object) {
		// Makes sense for all titles to be searchable in Elastic under one "title" field, so that's why it might be an array, here.
		if (Array.isArray(object.title)) {
			formattedObject.title = object.title[0];
		} else {
			formattedObject.title = object.title;
		}
	} else if ('titles' in object) {
		formattedObject.title = object.titles?.primary?.title;
	}

	//webLabel
	if ('webLabel' in object) {
		formattedObject.webLabel = object.webLabel;
	}

	//extendedStatuses
	if ('extendedStatuses' in object) {
		formattedObject.extendedStatuses = object.extendedStatuses;
	} else {
		formattedObject.extendedStatuses = [];
	}

	if ('url' in object) {
		// make absolute URLs relative because we have more environments than just production, it turns out
		formattedObject.url = object.url?.startsWith('/')
			? object.url
			: new URL(object.url).pathname;
	} else if ('objectID' in object) {
		formattedObject.url = routes.object(object.objectID);
	} else if ('crdId' in object) {
		formattedObject.url = routes.object(object.crdId);
	}

	// srcset
	if ('thumbnailUrl' in object) {
		// images subdomain will reliabily follow this pattern 👍
		// collectionapi.metmuseum.org images will not, so they must look like crap for now 😭
		if (object.thumbnailUrl?.startsWith('https://images.metmuseum.org/')) {
			const imageSources = {
				225: object.thumbnailUrl,
				480: object.thumbnailUrl?.replace('/web-additional/', '/mobile-large/'),
				575: object.thumbnailUrl?.replace(
					'/web-additional/',
					'/web-highlight/',
				),
				600: object.thumbnailUrl?.replace('/web-additional/', '/web-large/'),
			};

			let srcSet = '';
			for (const [size, url] of Object.entries(imageSources)) {
				srcSet += `${url} ${size}w, `;
			}

			formattedObject.imageSrcSet = srcSet;
		}

		// extended statuses (loans, deaccessioned, etc.)
		if ('extendedStatuses' in object) {
			formattedObject.extendedStatuses = object.extendedStatuses;
		}
	}

	return formattedObject;
};
